/****************************************************************
 * Autocomplete Input Component
 * A reusable autocomplete/combobox component for flocking options
 ****************************************************************/

class AutocompleteInput extends HTMLElement {
  constructor() {
    super();

    // Options
    this.options = {
      hiddenClass: 'hidden',
      maxResults: 10,
      minCharacters: 0,
      debounceTime: 150,
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    // Data and state
    this.allOptions = [];
    this.filteredOptions = [];
    this.selectedOption = null;
    this.currentFocus = -1;
    this.isOpen = false;

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    // Get elements
    this.input = this.querySelector('[data-autocomplete-input]');
    this.dropdown = this.querySelector('[data-autocomplete-dropdown]');
    this.optionsContainer = this.querySelector('[data-autocomplete-options]');
    this.noResultsElement = this.querySelector('[data-autocomplete-no-results]');
    this.hiddenInput = this.querySelector('[data-autocomplete-hidden-input]');
    this.selectedImageContainer = this.querySelector('[data-autocomplete-selected-image]');
    this.selectedImage = this.querySelector('[data-autocomplete-selected-img]');

    if (!this.input || !this.dropdown || !this.optionsContainer) {
      console.error('AutocompleteInput: Required elements not found');
      return;
    }

    this.loadOptions();
    this.bindEvents();

    // Initialize with all options visible (combobox behavior)
    this.renderOptions(this.allOptions);
  }

  /**
   * Load options from data attribute
   */
  loadOptions() {
    const optionsData = this.dataset.autocompleteOptions;
    try {
      if (optionsData) {
        this.allOptions = JSON.parse(optionsData).items;
        this.filteredOptions = [...this.allOptions];
      }
    } catch (error) {
      console.error('AutocompleteInput: Error parsing options data', error, { optionsData });
      this.allOptions = [];
      this.filteredOptions = [];
    }
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Input events
    this.input.addEventListener(
      'input',
      debounce(this.handleInput.bind(this), this.options.debounceTime),
    );
    this.input.addEventListener('focus', this.handleFocus.bind(this));
    this.input.addEventListener('blur', this.handleBlur.bind(this));
    this.input.addEventListener('keydown', this.handleKeydown.bind(this));

    // Click outside to close
    document.addEventListener('click', this.handleDocumentClick.bind(this));

    // Window resize to update dropdown width
    window.addEventListener('resize', debounce(this.handleResize.bind(this), 100));
  }

  /**
   * Handle input changes
   */
  handleInput(event) {
    const value = event.target.value.trim();
    this.filterOptions(value);
    this.open();
  }

  /**
   * Handle input focus
   */
  handleFocus() {
    if (!this.isOpen) {
      this.open();
    }
  }

  /**
   * Handle input blur (with delay to allow for option selection)
   */
  handleBlur() {
    // Delay to allow for option clicks
    setTimeout(() => {
      this.close();
    }, 150);
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(event) {
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const options = this.optionsContainer.querySelectorAll('[data-autocomplete-option]');
    const optionsCount = options.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.currentFocus = Math.min(this.currentFocus + 1, optionsCount - 1);
        this.updateFocus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.currentFocus = Math.max(this.currentFocus - 1, -1);
        this.updateFocus();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.currentFocus >= 0 && options[this.currentFocus]) {
          this.selectOption(this.filteredOptions[this.currentFocus]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.close();
        this.input.blur();
        break;

      case 'Tab':
        this.close();
        break;
    }
  }

  /**
   * Handle clicks outside the component
   */
  handleDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.close();
    }
  }

  /**
   * Handle window resize to update dropdown width
   */
  handleResize() {
    if (this.isOpen) {
      const containerWidth = this.getBoundingClientRect().width;
      this.dropdown.style.width = `${containerWidth}px`;
    }
  }

  /**
   * Filter options based on search term
   */
  filterOptions(searchTerm) {
    if (searchTerm.length <= this.options.minCharacters) {
      this.filteredOptions = [...this.allOptions];
    } else {
      const matchPercentageMap = new Map();
      this.filteredOptions = this.allOptions
        .filter((option) => {
          const matchPercentage = getFuzzyMatchPercentage(option.name, searchTerm);
          matchPercentageMap.set(option, matchPercentage);

          return matchPercentage > 60;
        })
        .sort((a, b) => matchPercentageMap.get(b) - matchPercentageMap.get(a));
    }

    // Limit results
    if (this.options.maxResults > 0) {
      this.filteredOptions = this.filteredOptions.slice(0, this.options.maxResults);
    }

    this.renderOptions(this.filteredOptions);
    this.currentFocus = -1;
  }

  /**
   * Render options in the dropdown
   */
  renderOptions(options) {
    // Clear current options
    this.optionsContainer.innerHTML = '';

    if (options.length === 0) {
      this.showNoResults();
      return;
    }

    this.hideNoResults();

    // Create option elements
    options.forEach((option, index) => {
      const optionElement = this.createOptionElement(option, index);
      this.optionsContainer.appendChild(optionElement);
    });

    // Update ARIA attributes
    this.updateAriaAttributes();
  }

  /**
   * Create a single option element
   */
  createOptionElement(option, index) {
    const optionElement = document.createElement('div');
    optionElement.className =
      'autocomplete__option flex cursor-pointer items-center gap-4 border-t border-gray-4 py-2 px-3 first-of-type:border-t-0 hover:bg-gray-4 focus:bg-gray-4';
    optionElement.setAttribute('data-autocomplete-option', '');
    optionElement.setAttribute('data-option-id', option.id);
    optionElement.setAttribute('role', 'option');
    optionElement.setAttribute('aria-selected', 'false');
    optionElement.setAttribute('id', `option-${this.id || 'autocomplete'}-${index}`);

    // Add option content
    let content = '';

    // Add image if available (for player options)
    if (option.image) {
      content += `<div class="shrink-0">
        <img
            src="${option.image}"
            alt=""
            class="w-8 h-8 rounded object-cover" loading="lazy"
            onerror="this.style.visibility='hidden';"
        >
      </div>`;
    }

    // Add text content container
    content += `<div class="flex-1">
      <div class="flex flex-wrap content-center text-black">${option.name}</div>`;

    // Add player number if available
    if (option.number) {
      content += `<div class="text-sm text-gray-2">#${option.number}</div>`;
    }

    content += `</div>`;

    // Add price if available
    if (option.price !== undefined) {
      content += `<div class="flex flex-wrap content-center text-sm text-gray-2 ml-auto">+ ${this.formatPrice(
        option.price,
      )}</div>`;
    }

    optionElement.innerHTML = content;

    // Add click event
    optionElement.addEventListener('click', () => {
      this.selectOption(option);
    });

    return optionElement;
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    // Use Shopify's money format if available, otherwise simple format
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    return `€${(price / 100).toFixed(2)}`;
  }

  /**
   * Select an option
   */
  selectOption(option) {
    this.selectedOption = option;
    this.input.value = option.name;
    this.hiddenInput.value = option.id;

    // Update data attributes for configurator integration
    this.input.dataset.selectedOptionId = option.id;
    this.input.dataset.selectedOptionName = option.name;
    this.input.dataset.selectedOptionPrice = option.price || '';

    // Show selected image if available
    this.updateSelectedImage(option);

    // Dispatch change event for configurator
    const changeEvent = new CustomEvent('autocomplete:change', {
      detail: { option },
      bubbles: true,
    });
    this.dispatchEvent(changeEvent);

    this.close();
    this.input.blur();
  }

  /**
   * Update the selected image display
   */
  updateSelectedImage(option) {
    if (!this.selectedImageContainer || !this.selectedImage) {
      return;
    }

    if (option && option.image) {
      this.selectedImage.src = option.image;
      this.selectedImage.alt = option.name || '';
      // `hidden` class is toggled via onload/onerror in the HTML to ensure image is loaded
    } else {
      this.selectedImageContainer.classList.add('hidden');
      this.selectedImage.src =
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E";
      this.selectedImage.alt = '';
    }
  }

  /**
   * Update focus state for keyboard navigation
   */
  updateFocus() {
    const options = this.optionsContainer.querySelectorAll('[data-autocomplete-option]');

    options.forEach((option, index) => {
      if (index === this.currentFocus) {
        option.classList.add('autocomplete__option--focused');
        option.setAttribute('aria-selected', 'true');
        option.scrollIntoView({ block: 'nearest' });

        // Update input aria-activedescendant
        this.input.setAttribute('aria-activedescendant', option.id);
      } else {
        option.classList.remove('autocomplete__option--focused');
        option.setAttribute('aria-selected', 'false');
      }
    });

    if (this.currentFocus === -1) {
      this.input.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Open the dropdown
   */
  open() {
    if (this.isOpen) return;

    this.isOpen = true;

    // Explicitly set dropdown width for Safari compatibility
    const containerWidth = this.getBoundingClientRect().width;
    this.dropdown.style.width = `${containerWidth}px`;

    this.dropdown.classList.remove(this.options.hiddenClass);
    this.input.setAttribute('aria-expanded', 'true');

    // Update focus if no options are filtered
    if (this.filteredOptions.length > 0) {
      this.currentFocus = -1;
      this.updateFocus();
    }
  }

  /**
   * Close the dropdown
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.dropdown.classList.add(this.options.hiddenClass);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.currentFocus = -1;
  }

  /**
   * Show no results message
   */
  showNoResults() {
    if (this.noResultsElement) {
      this.noResultsElement.classList.remove(this.options.hiddenClass);
    }
  }

  /**
   * Hide no results message
   */
  hideNoResults() {
    if (this.noResultsElement) {
      this.noResultsElement.classList.add(this.options.hiddenClass);
    }
  }

  /**
   * Update ARIA attributes
   */
  updateAriaAttributes() {
    const optionsCount = this.filteredOptions.length;
    this.input.setAttribute('aria-label', `${optionsCount} options available`);
  }

  /**
   * Clear the selection
   */
  clear() {
    this.selectedOption = null;
    this.input.value = '';
    this.hiddenInput.value = '';
    this.input.removeAttribute('data-selected-option-id');
    this.input.removeAttribute('data-selected-option-name');
    this.input.removeAttribute('data-selected-option-price');

    // Hide selected image
    this.updateSelectedImage(null);

    // Reset to show all options
    this.filterOptions('');

    // Dispatch clear event
    const clearEvent = new CustomEvent('autocomplete:clear', {
      bubbles: true,
    });
    this.dispatchEvent(clearEvent);
  }

  /**
   * Get the currently selected option
   */
  getSelectedOption() {
    return this.selectedOption;
  }

  /**
   * Set an option programmatically
   */
  setOption(optionId) {
    const option = this.allOptions.find((opt) => opt.id === optionId);
    if (option) {
      this.selectOption(option);
    }
  }
}

// Register the custom element
customElements.define('autocomplete-input', AutocompleteInput);

export default AutocompleteInput;
